import type { AccessToken, AuthorizationServer, AuthRepository } from '@taler/api-client'
import { createAuthorizationRequest, readCallback } from '@taler/api-client'
import type { TransactionStore } from './transaction-store.ts'

export interface SignInOptions {
  readonly server: AuthorizationServer
  readonly transactions: TransactionStore
  readonly auth: AuthRepository
  readonly navigate: (url: string) => void
}

export type CompletionOutcome =
  | { readonly kind: 'signed-in'; readonly token: AccessToken; readonly returnTo: string }
  | { readonly kind: 'declined'; readonly error: string }
  | { readonly kind: 'restart'; readonly reason: 'no-transaction' | 'state-mismatch' | 'unusable-callback' }

export interface SignIn {
  begin(returnTo: string): Promise<void>
  complete(callbackUrl: string): Promise<CompletionOutcome>
}

export function createSignIn(options: SignInOptions): SignIn {
  return {
    async begin(returnTo) {
      const request = await createAuthorizationRequest(options.server)
      options.transactions.put({ state: request.state, verifier: request.verifier, returnTo })
      options.navigate(request.url)
    },

    async complete(callbackUrl) {
      const callback = readCallback(callbackUrl)
      const transaction = options.transactions.take()

      if (callback.kind === 'declined') return { kind: 'declined', error: callback.error }
      if (callback.kind === 'unusable') return { kind: 'restart', reason: 'unusable-callback' }
      if (transaction === null) return { kind: 'restart', reason: 'no-transaction' }
      // A callback whose state does not match the one this tab issued is not ours.
      if (transaction.state !== callback.state) return { kind: 'restart', reason: 'state-mismatch' }

      const token = await options.auth.exchange({
        code: callback.code,
        codeVerifier: transaction.verifier,
        redirectUri: options.server.redirectUri,
        client: 'browser',
      })
      return { kind: 'signed-in', token, returnTo: transaction.returnTo }
    },
  }
}
